import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Card, Divider, Button, message, type TableProps } from "antd"
import { ReloadOutlined } from "@ant-design/icons"
import { IconSparkles } from "@tabler/icons-react"
import type React from "react"
import AiWordSearchForm from "./components/AiWordSearchForm"
import AiWordTable from "./components/AiWordTable"
import AiWordDetailModal from "./components/AiWordDetailModal"
import { aiWordService, type AiWordSearchParams, type AiWordItem } from "@/services/log/aiWordApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

export const AiWordPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useTableParams<AiWordSearchParams>({
    ...PAGE_CONFIG,
    search: "",
    sort_by: "search_count",
    sort_order: "DESC",
  })

  const [selectedRecord, setSelectedRecord] = useState<AiWordItem | null>(null)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["ai_words_list", searchParams],
    queryFn: () => {
      return aiWordService.list({
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search || undefined,
        sort_by: searchParams.sort_by,
        sort_order: searchParams.sort_order,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => aiWordService.delete(id),
    onSuccess: () => {
      message.success("Xóa từ vựng AI thành công!")
      queryClient.invalidateQueries({ queryKey: ["ai_words_list"] })
    },
    onError: (err: Error) => {
      message.error(err?.message || "Có lỗi xảy ra khi xóa từ vựng!")
    },
  })

  const handleSearch = (values: Partial<AiWordSearchParams>) => {
    setSearchParams({
      ...searchParams,
      ...values,
      page: 1,
    })
  }

  const handleTableChange: TableProps<AiWordItem>["onChange"] = (pagination, _filters, sorter) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter

    let nextSortBy = searchParams.sort_by
    let nextSortOrder = searchParams.sort_order

    if (singleSorter?.order) {
      nextSortBy = String(singleSorter.field || singleSorter.columnKey)
      nextSortOrder = singleSorter.order === "ascend" ? "ASC" : "DESC"
    }

    setSearchParams({
      ...searchParams,
      page: pagination.current || 1,
      per_page: pagination.pageSize || searchParams.per_page,
      sort_by: nextSortBy,
      sort_order: nextSortOrder,
    })
  }

  const items = result?.data || []
  const totalCount = result?.paginate?.total ?? 0

  return (
    <div className="flex flex-col gap-2">
      <AiWordSearchForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          sort_by: searchParams.sort_by,
          sort_order: searchParams.sort_order,
        }}
      />

      <Card
        className="flex min-h-0 grow flex-col shadow-xs"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              <IconSparkles className="text-amber-500" size={20} />
              <span className="font-semibold text-base text-gray-800">Từ điển AI Fallback (AI Words Pool)</span>
              <Divider orientation="vertical" />
              <span className="font-normal text-gray-500 text-sm">
                Tổng số từ đã lưu: <strong className="text-gray-700">{totalCount}</strong>
              </span>
            </div>
            <Button icon={<ReloadOutlined spin={isFetching} />} onClick={() => refetch()} loading={isFetching}>
              Làm mới
            </Button>
          </div>
        }
      >
        <AiWordTable
          data={items}
          loading={isFetching}
          onViewDetail={(record) => setSelectedRecord(record)}
          onDelete={(id) => deleteMutation.mutate(id)}
          sortField={searchParams.sort_by}
          sortOrder={searchParams.sort_order}
          onChange={handleTableChange}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total: totalCount,
            ...PAGINATION_CONFIG,
          }}
        />
      </Card>

      <AiWordDetailModal open={!!selectedRecord} record={selectedRecord} onClose={() => setSelectedRecord(null)} />
    </div>
  )
}

export default AiWordPage
